import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, PenLine, Mic, Music, Video } from 'lucide-react';
import api, { mediaUrl, errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import BlogComposer from '../../components/BlogComposer';

const ContentStudio = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | post | null

  const fetchAll = async () => {
    setLoading(true);
    try { const r = await api.get('/my-blog/posts'); setItems(r.data || []); }
    catch (e) { console.error('load posts failed', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const remove = async (p) => {
    if (!window.confirm('Delete this draft?')) return;
    try { await api.delete(`/my-blog/posts/${p.id}`); toast({ title: 'Deleted' }); fetchAll(); }
    catch (e) { toast({ title: 'Delete failed', description: errText(e) }); }
  };

  if (editing) {
    return (
      <div className="max-w-3xl space-y-5" data-testid="studio-editor">
        <div className="flex items-center justify-between">
          <h1 className="font-serif-display text-3xl text-burgundy">{editing === 'new' ? 'New Post' : 'Edit Post'}</h1>
        </div>
        <p className="text-mahogany/70 text-sm">Your post is saved as a <strong>draft</strong>. An admin reviews and publishes it.</p>
        <BlogComposer
          initial={editing === 'new' ? null : editing}
          createEndpoint="/my-blog/posts"
          updateEndpoint={(id) => `/my-blog/posts/${id}`}
          canPublish={false}
          onSaved={() => { setEditing(null); fetchAll(); }}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="content-studio-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2"><PenLine className="w-4 h-4" /> Content Studio</div>
          <h1 className="font-serif-display text-4xl text-burgundy">Your <span className="font-italic-display text-rose-italic">Writing</span></h1>
          <p className="mt-2 text-mahogany/80">Draft articles, audio blogs and vlogs with AI &amp; voice. Admins publish them.</p>
        </div>
        <button onClick={() => setEditing('new')} className="pill-btn" data-testid="studio-new-btn"><Plus className="w-4 h-4" /> New Post</button>
      </div>

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : items.length === 0 ? <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40">No drafts yet. Start your first post.</div>
        : (
          <ul className="space-y-3" data-testid="studio-list">
            {items.map((p) => (
              <li key={p.id} className="p-4 rounded-2xl border border-rose/50 bg-white/70 flex flex-wrap items-center justify-between gap-4" data-testid={`studio-post-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {p.cover_image && <img src={mediaUrl(p.cover_image)} alt="" className="w-14 h-14 rounded-lg object-cover" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif-display text-lg text-burgundy truncate">{p.title}</span>
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{p.category}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.is_published ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{p.is_published ? 'Published' : 'Draft'}</span>
                      {p.audio_url && <Music className="w-3.5 h-3.5 text-mahogany/60" title="Audio blog" />}
                      {p.video_url && <Video className="w-3.5 h-3.5 text-mahogany/60" title="Vlog" />}
                    </div>
                    <div className="text-xs text-mahogany/60 mt-0.5">/{p.slug}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!p.is_published && <IconBtn onClick={() => setEditing(p)} title="Edit"><Pencil className="w-4 h-4" /></IconBtn>}
                  {!p.is_published && <IconBtn onClick={() => remove(p)} title="Delete" danger><Trash2 className="w-4 h-4" /></IconBtn>}
                  {p.is_published && <span className="text-xs text-mahogany/50">Published — contact admin to edit</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

const IconBtn = ({ children, danger, ...p }) => (
  <button {...p} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${danger ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-rose-soft/60 text-mahogany hover:bg-rose-soft'}`}>{children}</button>
);

export default ContentStudio;
